// pages/reports/StockInfoReport.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Fade, TableRow, TableCell, alpha, Typography,
} from '@mui/material';
import { Inventory as Icon } from '@mui/icons-material';
import ReportLayout, { ReportColumn, SummaryCard, fmt2 } from '../../components/reports/ReportLayout';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import { reportService, StockRow } from '../../services/reportService';
import { tradesService } from '../../services/tradesService';
import { fmtBD } from '../../components/common/bdNumber';

const ACCENT = '#224248';

// same rule as PivotInfoReport: 600 -> "600", 599.5 -> "599.5"
const fmtLitre = (v: number | null | undefined): string => {
  const n = Number(v ?? 0);
  return Number.isInteger(n) ? fmtBD(n, 0) : fmtBD(n, 1);
};

// pcs / litre cell, pivot style: "<pcs> / <litre> L"
const qtyLitre = (pcs: number | null | undefined, litre: number | null | undefined): string =>
  `${fmt2(pcs ?? 0)} Pcs / ${fmtLitre(litre)} L`;

const qtyLitreSum = (pcs: number | null | undefined, litre: number | null | undefined): string =>
  `${fmt2(pcs ?? 0)} Pcs \n${fmtLitre(litre)} L`;

const StockInfoReport: React.FC = () => {
  const [rows, setRows]       = useState<StockRow[]>([]);
  const [totals, setTotals]   = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<{ value: number; label: string }[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({ product_id: '' });

  useEffect(() => {
    tradesService.listProducts({ is_active: true })
      .then(r => setProducts(r.results.map(p => ({ value: p.product_id, label: p.product_name }))))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.product_id) params.product_id = filters.product_id;
      const res = await reportService.getStockReport(params);
      setRows(res.results);
      setTotals((res.totals || {}) as any);
    } catch { } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = (fmt: 'excel' | 'pdf') => {
    const params: any = {};
    if (filters.product_id) params.product_id = filters.product_id;
    return reportService.downloadStock(fmt, params);
  };

  // Pre-compute the combined "<pcs> / <litre> L" strings per row so each column's
  // single-arg `format(v)` has everything it needs (no dependence on a row arg).
  const displayRows = useMemo(
    () =>
      rows.map(r => ({
        ...r,
        _purchase: qtyLitre(r.purchase, r.purchase_litre),
        _sell:     qtyLitre(r.sell,     r.sell_litre),
        _return:   qtyLitre(r.return,   r.return_litre),
        _stock:    qtyLitre(r.stock,    r.stock_litre),
      })),
    [rows],
  );

  const columns: ReportColumn[] = [
    { key: 'sl',              label: '#',            align: 'center', width: 50 },
    { key: 'product_name',    label: 'Product Name', width: 180 },
    { key: 'category_name',   label: 'Category', width: 100,    format: v => v || '—' },
    // { key: 'unit_of_measure', label: 'UOM',          align: 'center' },
    { key: '_purchase', label: 'Purchase', align: 'right', width: 180, 
      format: v => <Typography fontWeight={600} fontSize="0.8rem" color={ACCENT}>{v}</Typography> },
    { key: '_sell',     label: 'Sell',     align: 'right', width: 180, 
      format: v => <Typography fontWeight={600} fontSize="0.8rem" color="#4CAF50">{v}</Typography> },
    { key: '_return',   label: 'Return',   align: 'right', width: 150, 
      format: v => <Typography fontWeight={600} fontSize="0.8rem" color="#FF9800">{v}</Typography> },
    { key: '_stock',    label: 'Stock',    align: 'right', width: 180, 
      format: v => <Typography fontWeight={700} fontSize="0.8rem" color="#9C27B0">{v}</Typography> },
    { key: 'purchase_rate',  label: 'Latest Purchase Rate (Tk.)', align: 'right', width: 50, format: v => fmt2(v) },
    { key: 'purchase_total', label: 'Purchase Total (Tk.)', align: 'right', width: 50, format: v => <strong>{fmt2(v)}</strong> },
  ];

  const summaryCards: SummaryCard[] = [
    { label: 'Total Products',    value: rows.length,                                                     color: '#44A1A4'     },
    { label: 'Total Purchase',    value: qtyLitreSum(totals.total_purchase, totals.total_purchase_litre),    color:  ACCENT   },
    { label: 'Total Sell',        value: qtyLitreSum(totals.total_sell,     totals.total_sell_litre),        color: '#4CAF50'  },
    { label: 'Total Return',      value: qtyLitreSum(totals.total_return,   totals.total_return_litre),      color: '#FF9800'  },
    { label: 'Total Stock',       value: qtyLitreSum(totals.total_stock,    totals.total_stock_litre),       color: '#9C27B0'  },
    { label: 'Stock Value (Tk.)', value: fmt2(totals.total_amount ?? 0),                                  color: '#f44336'  },
  ];

  const totalsRow = rows.length > 0 && !loading ? (
    <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
      <TableCell colSpan={3} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>GRAND TOTAL</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: ACCENT }}>{qtyLitre(totals.total_purchase, totals.total_purchase_litre)}</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#4CAF50' }}>{qtyLitre(totals.total_sell,     totals.total_sell_litre)}</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800' }}>{qtyLitre(totals.total_return,   totals.total_return_litre)}</TableCell>
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#9C27B0' }}>{qtyLitre(totals.total_stock,    totals.total_stock_litre)}</TableCell>
      <TableCell />
      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#f44336' }}>{fmt2(totals.total_amount ?? 0)}</TableCell>
    </TableRow>
  ) : undefined;

  return (
    <Fade in timeout={400}>
      <div>
        <ReportLayout
          title="Stock Information Report"
          subtitle="Current stock levels per product after purchase, sell and return"
          accentColor={ACCENT}
          columns={columns}
          rows={displayRows}
          loading={loading}
          tableMinWidth={1500}
          summaryCards={summaryCards}
          onRefresh={fetchData}
          onDownload={handleDownload}
          totalsRow={totalsRow}
          emptyIcon={<Icon sx={{ fontSize: 40 }} />}
          filters={
            <ReportFilterBar
              accentColor={ACCENT}
              values={filters}
              onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
              onClear={() => setFilters({ product_id: '' })}
              fields={[
                { type: 'select', key: 'product_id', label: 'Product', options: products, minWidth: 280 },
              ]}
            />
          }
        />
      </div>
    </Fade>
  );
};
export default StockInfoReport;